/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/
import React from 'react';
import PropTypes from 'prop-types';

import { Asset } from '@react/react-spectrum/Asset';
import { Card, CardBody, CardPreview } from '@react/react-spectrum/Card';
import { useIntl } from 'react-intl';

import classes from './Cards.scss';

const ProductCard = React.forwardRef((props, ref) => {
    const { product, ...otherProps } = props;
    const { formatNumber } = useIntl();

    const preview =
        product.thumbnail && product.thumbnail.url ? (
            <Asset smartness={1} type="image" src={product.thumbnail.url.replace('http://', '//')} alt={product.name} />
        ) : (
            <Asset type="file" />
        );

    let className = props.onClick ? classes.clickable : '';

    let { value, currency } =
        product.price_range && product.price_range.minimum_price && product.price_range.minimum_price.final_price
            ? product.price_range.minimum_price.final_price
            : {};

    return (
        <Card {...otherProps} forwardedRef={ref} className={className}>
            <CardPreview>{preview}</CardPreview>
            <CardBody
                title={product.name}
                subtitle={product.sku}
                description={value && formatNumber(value, { style: 'currency', currency })}
            />
        </Card>
    );
});

ProductCard.displayName = 'ProductCard';

ProductCard.propTypes = {
    onClick: PropTypes.func,
    product: PropTypes.shape({
        thumbnail: PropTypes.shape({
            url: PropTypes.string
        }),
        name: PropTypes.string.isRequired,
        sku: PropTypes.string.isRequired,
        price_range: PropTypes.shape({
            minimum_price: PropTypes.shape({
                final_price: PropTypes.shape({
                    value: PropTypes.number,
                    currency: PropTypes.string
                })
            })
        })
    })
};

export default ProductCard;
